import { useState } from 'react';
import { useLanguage } from '../i18n';
import { useInterview } from './InterviewContext';
import { Send, Lightbulb, X, Sparkles } from 'lucide-react';

export default function InterviewQuestion() {
  const { t } = useLanguage();
  const i = t.interview;
  const { step, sending, answer, error, clarification, clarifying, recovering, next, clarify } = useInterview();
  const [text, setText] = useState('');
  const [clarificationOpen, setClarificationOpen] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!text.trim() || sending) return;
    const ok = await answer(text.trim());
    if (ok) setText('');
  };

  if (step.kind === 'loading' || recovering) {
    return (
      <div className="bg-white border border-cloud p-4 sm:p-8 text-center">
        <div className="text-slate text-sm">{i.questionGenerating}</div>
      </div>
    );
  }

  if (step.kind === 'idle') {
    return (
      <div className="bg-white border border-cloud p-4 sm:p-6 text-center space-y-3">
        <p className="text-slate text-sm">{i.questionStuck}</p>
        <button
          type="button"
          onClick={() => void next()}
          disabled={recovering}
          className="min-h-[44px] px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-medium inline-flex items-center gap-2 transition-colors disabled:opacity-50"
        >
          <Sparkles size={16} /> {i.questionContinue}
        </button>
      </div>
    );
  }

  if (step.kind === 'failed') {
    return (
      <div className="bg-amber-50 border border-amber-200 p-4 sm:p-5">
        <p className="font-medium text-amber-900 mb-1">{i.questionFailed}</p>
        <p className="text-sm text-amber-800">{step.message}</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-3 px-4 py-2 min-h-[44px] text-sm font-medium bg-amber-600 hover:bg-amber-700 text-white"
        >
          {i.questionRetry}
        </button>
      </div>
    );
  }

  if (step.kind === 'done') {
    return (
      <div className="bg-emerald-50 border border-emerald-200 p-4 sm:p-5">
        <p className="font-medium text-emerald-900">{i.sessionEndConfirm.split('?')[0]}.</p>
      </div>
    );
  }

  return (
    <div className="bg-white border border-cloud p-4 sm:p-6 space-y-4">
      {error && error.kind === 'update' && (
        <div className="bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">{error.message}</div>
      )}
      {error && error.kind === 'next' && (
        <div className="bg-amber-50 border border-amber-200 p-3 text-sm text-amber-900">{error.message}</div>
      )}
      {error && error.kind === 'planLimit' && (
        <div className="bg-red-50 border border-red-200 p-3 text-sm text-red-900">{error.message}</div>
      )}

      <div>
        <div className="text-xs font-semibold text-red-600 uppercase tracking-wider mb-2">Question</div>
        <p className="text-base sm:text-lg text-ink leading-relaxed font-medium">{step.question}</p>
      </div>

      {clarificationOpen && (
        <div className="bg-paper border border-cloud p-4 relative">
          <button
            type="button"
            onClick={() => setClarificationOpen(false)}
            className="absolute top-2 right-2 p-1 text-ash hover:text-ink min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label="Close clarification"
          >
            <X size={16} />
          </button>
          <div className="text-xs font-semibold text-slate uppercase tracking-wider mb-1.5">Clarification</div>
          <p className="text-sm text-slate leading-relaxed whitespace-pre-wrap pr-8">
            {clarifying ? '...' : clarification || i.clarifyFailed}
          </p>
        </div>
      )}

      <form onSubmit={submit} className="space-y-3">
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={i.answerPlaceholder}
          rows={5}
          disabled={sending}
          className="w-full px-3 py-2 border border-cloud bg-paper text-base focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-red-500 disabled:opacity-50"
        />
        <div className="flex items-center justify-between gap-2 flex-wrap sm:flex-nowrap">
          <button
            type="button"
            onClick={async () => {
              setClarificationOpen(true);
              await clarify();
            }}
            disabled={clarifying}
            className="min-h-[44px] px-3 py-2 text-sm font-medium text-slate hover:bg-mist border border-cloud flex items-center gap-1.5 transition-colors disabled:opacity-50"
          >
            <Lightbulb size={14} /> {clarifying ? '...' : i.clarify}
          </button>
          <button
            type="submit"
            disabled={!text.trim() || sending}
            className="min-h-[44px] px-4 py-2 bg-red-500 hover:bg-red-600 text-white font-medium flex items-center gap-2 transition-colors disabled:opacity-50"
          >
            <Send size={14} /> {sending ? i.answerSending : i.answerSend}
          </button>
        </div>
      </form>
    </div>
  );
}
